import React, { useContext, useState } from 'react';
import { useQuery } from 'react-query';

import { CloseOutlined } from '@ant-design/icons';
import { Form } from 'antd';
import styled from 'styled-components';

import { bulkAdditionalLinks, getAdditionalLinks } from 'apis/CommunityAPI';
import CommunityInfoConfirmationModal from 'components/CommunityInfo/CommunityInfoConfirmationModal';
import { useRootContext } from 'components/layout/RootContext';
import Input from 'components/lib/Input';

import ShopDataModalAddButtonPill from '../../../ShopData/common/ShopDataModalAddButtonPill';
import { AdditionalLinksType } from '../../../ShopData/types';
import { useEditDataModalContext } from '../../common/edit/EditDataModalContext';
import { CommunityInfoContext } from '../../CommunityInfoContext';

const Container = styled.div`
  display: flex;
  width: 800px;
  flex-direction: column;
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
`;

const StyledTitle = styled.div`
  font-family: var(--font-bold);
  margin-bottom: 7px;
  margin-top: 15px;
  width: 300px;
`;

const Row = styled.div`
  display: flex;
  gap: 10px;
  margin-bottom: 8px;
  padding: 0px 23px 0px 30px;
`;

const ActionContainer = styled.div`
  padding: 15px 0px 30px 30px;
  background: var(--white);
  text-align: left;
`;

const RemoveIconContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
`;

const Links = styled.div`
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  flex-grow: 1;
`;

type Props = {
  community?: any;
  disabled?: boolean;
};

const AdditionalLinksEdit = ({ community, disabled = false }: Props) => {
  const { showMessage } = useRootContext();
  const { communityInfo } = useContext(CommunityInfoContext);
  const { onSuccess, modified, setModified, setLoading, form } = useEditDataModalContext();
  const [showConfirmation, setShowConfirmation] = useState(false);

  const { data, isFetched, refetch } = useQuery({
    queryKey: ['community', 'additional', 'links', communityInfo?.id, community],
    queryFn: () => getAdditionalLinks(communityInfo?.id || community.id),
    onError: () => {
      showMessage('error', 'Failed to fetch community data.');
    },
    enabled: Boolean(communityInfo || community),
  });

  const handleConfirmSubmit = async () => {
    try {
      const values = await form?.validateFields();
      setLoading(true);

      const payload = (values?.additionalLinks ?? []).map((item: AdditionalLinksType) => ({
        ...item,
        apartment: communityInfo?.id || community?.id,
      }));

      await bulkAdditionalLinks(communityInfo?.id || community?.id, payload).then((response) => {
        showMessage('success', 'Community Additional Links added successfully');
        onSuccess(response);
        refetch();
      });
    } catch (error) {
      showMessage('error', 'Trouble updating community Additional Links!');
    } finally {
      setLoading(false);
      setShowConfirmation(false);
    }
  };

  const handleValuesChanged = () => {
    if (!modified) {
      setModified(true);
    }
  };

  return (
    <Container>
      {isFetched && data && (
        <Form
          form={form}
          initialValues={{ additionalLinks: data }}
          disabled={disabled}
          onValuesChange={handleValuesChanged}
          onFinish={() => setShowConfirmation(true)}
        >
          <Form.List name="additionalLinks">
            {(fields, { add, remove }) => (
              <Content>
                <Links className="mini-scroll">
                  <Row>
                    <StyledTitle>DESCRIPTION</StyledTitle>
                    <StyledTitle>URL</StyledTitle>
                  </Row>
                  {fields.map(({ key, name, ...restField }) => (
                    <Row key={key}>
                      <Form.Item name={[name, 'id']} hidden>
                        <Input />
                      </Form.Item>
                      <Form.Item
                        {...restField}
                        name={[name, 'description']}
                        style={{ width: '300px' }}
                        preserve
                        rules={[{ required: true, message: 'Please enter a description' }]}
                      >
                        <Input style={{ width: '100%', height: 36 }} placeholder="Enter a description" />
                      </Form.Item>

                      <Form.Item
                        {...restField}
                        name={[name, 'page_url']}
                        style={{ width: '400px' }}
                        preserve
                        rules={[{ required: true, message: 'Please enter a url' }]}
                      >
                        <Input style={{ width: '100%', height: 36 }} placeholder="Enter a url" />
                      </Form.Item>
                      <RemoveIconContainer>
                        <CloseOutlined
                          onClick={() => remove(name)}
                          style={{ color: 'var(--gray-dark)', fontSize: 12 }}
                        />
                      </RemoveIconContainer>
                    </Row>
                  ))}
                </Links>
                <ActionContainer>
                  <ShopDataModalAddButtonPill label={'Add Link'} onClick={() => add()} />
                </ActionContainer>
              </Content>
            )}
          </Form.List>
        </Form>
      )}
      <CommunityInfoConfirmationModal
        showCommunityIncentivesConfirmationModal={showConfirmation}
        communityName={communityInfo?.comunity_name}
        onConfirm={handleConfirmSubmit}
        onClose={() => setShowConfirmation(false)}
      />
    </Container>
  );
};

export default AdditionalLinksEdit;
